import { useInputs } from '@/app/medical-record/components/helpers/inputs';
import formatters from '@/filters';
import LABELS from './labels';

function buildInputs(definitions, inputHelper) {
  return definitions.map(({ name, formatter }) => {
    const value = inputHelper.getValue(name);
    return {
      name,
      value,
      formattedValue: formatter ? formatter(value) : value,
    };
  });
}

function makeRowBuilder(inputHelper) {
  return (label, definitions) => ({
    label,
    inputs: buildInputs(definitions, inputHelper),
  });
}

export function autoRefraction(inputs) {
  const data = {
    title: 'Auto Refração',
    columns: [
      { label: 'Esférica', highlight: true, align: 'center' },
      { label: 'Cilíndrica', highlight: true, align: 'center' },
      { label: 'Eixo', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('autoRef')) {
    const buildRow = makeRowBuilder(inputHelper);

    if (inputHelper.hasFilledWith('autoRefLegacy')) {
      data.rows = [
        buildRow(LABELS.R, [{ name: 'autoRefLegacyR' }]),
        buildRow(LABELS.L, [{ name: 'autoRefLegacyL' }]),
      ];
    } else {
      data.rows = [
        buildRow(LABELS.R, [
          { name: 'autoRefSphericalR', formatter: formatters.number },
          { name: 'autoRefCylindricalR', formatter: formatters.number },
          { name: 'autoRefAxisR', formatter: formatters.deg },
        ]),
        buildRow(LABELS.L, [
          { name: 'autoRefSphericalL', formatter: formatters.number },
          { name: 'autoRefCylindricalL', formatter: formatters.number },
          { name: 'autoRefAxisL', formatter: formatters.deg },
        ]),
      ];
    }
  }

  return data;
}

export function keratometry(inputs) {
  const data = {
    title: 'Ceratometria',
    columns: [
      { label: 'K1', highlight: false, align: 'center' },
      { label: 'Eixo K1', highlight: false, align: 'center' },
      { label: 'K2', highlight: false, align: 'center' },
      { label: 'Eixo K2', highlight: false, align: 'center' },
      { label: 'Cilindro', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('keratometry')) {
    const buildRow = makeRowBuilder(inputHelper);

    if (inputHelper.hasFilledWith('keratometryLegacy')) {
      data.rows = [
        buildRow(LABELS.R, [{ name: 'keratometryLegacyR' }]),
        buildRow(LABELS.L, [{ name: 'keratometryLegacyL' }]),
      ];
    } else {
      data.rows = [
        buildRow(LABELS.R, [
          { name: 'keratometryK1R', formatter: formatters.number },
          { name: 'keratometryK1AxisR', formatter: formatters.deg },
          { name: 'keratometryK2R', formatter: formatters.number },
          { name: 'keratometryK2AxisR', formatter: formatters.deg },
          { name: 'keratometryCylinderR', formatter: formatters.number },
        ]),
        buildRow(LABELS.L, [
          { name: 'keratometryK1L', formatter: formatters.number },
          { name: 'keratometryK1AxisL', formatter: formatters.deg },
          { name: 'keratometryK2L', formatter: formatters.number },
          { name: 'keratometryK2AxisL', formatter: formatters.deg },
          { name: 'keratometryCylinderL', formatter: formatters.number },
        ]),
      ];
    }
  }

  return data;
}

export function lensometry(inputs) {
  const data = {
    title: 'Lensometria',
    columns: [
      { label: 'Esférica', highlight: true, align: 'center' },
      { label: 'Cilíndrica', highlight: true, align: 'center' },
      { label: 'Eixo', highlight: false, align: 'center' },
      { label: 'Adição', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('lensometry')) {
    const buildRow = makeRowBuilder(inputHelper);

    if (inputHelper.hasFilledWith('lensometryLegacy')) {
      data.rows = [
        buildRow(LABELS.R, [{ name: 'lensometryLegacyR' }]),
        buildRow(LABELS.L, [{ name: 'lensometryLegacyL' }]),
      ];
    } else {
      data.rows = [
        buildRow(LABELS.R, [
          { name: 'lensometrySphericalR', formatter: formatters.number },
          { name: 'lensometryCylindricalR', formatter: formatters.number },
          { name: 'lensometryAxisR', formatter: formatters.deg },
          { name: 'lensometryAddition', formatter: formatters.number },
        ]),
        buildRow(LABELS.L, [
          { name: 'lensometrySphericalL', formatter: formatters.number },
          { name: 'lensometryCylindricalL', formatter: formatters.number },
          { name: 'lensometryAxisL', formatter: formatters.deg },
          { name: '' },
        ]),
      ];
    }
  }

  return data;
}

export function visualAcuity(inputs) {
  const data = {
    title: 'Acuídade Visual',
    columns: [
      { label: 'AV SC', highlight: false, align: 'center' },
      { label: 'AV CC', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('avsc|avcc')) {
    const buildRow = makeRowBuilder(inputHelper);
    const isAvscLegacy = inputHelper.hasFilledWith('avscLegacy');
    const isAvccLegacy = inputHelper.hasFilledWith('avccLegacy');

    data.rows = [
      buildRow(LABELS.R, [
        { name: isAvscLegacy ? 'avscLegacyR' : 'avscR' },
        { name: isAvccLegacy ? 'avccLegacyR' : 'avccR' },
      ]),
      buildRow(LABELS.L, [
        { name: isAvscLegacy ? 'avscLegacyL' : 'avscL' },
        { name: isAvccLegacy ? 'avccLegacyL' : 'avccL' },
      ]),
    ];
  }

  return data;
}

export function tonometry(inputs) {
  const data = {
    title: 'Pressão Intraocular',
    columns: [
      { label: 'Auto PIO', highlight: false, align: 'center' },
      { label: 'PIO', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('autoTonometry|tonometry')) {
    const buildRow = makeRowBuilder(inputHelper);
    const isAutoTonometryLegacy = inputHelper.hasFilledWith('autoTonometryLegacy');
    const isTonometryLegacy = inputHelper.hasFilledWith('tonometryLegacy');

    data.rows = [
      buildRow(LABELS.R, [
        {
          name: isAutoTonometryLegacy ? 'autoTonometryLegacyR' : 'autoTonometryR',
          formatter: !isAutoTonometryLegacy ? formatters.mmHg : null,
        },
        {
          name: isTonometryLegacy ? 'tonometryLegacyR' : 'tonometryR',
          formatter: !isTonometryLegacy ? formatters.mmHg : null,
        },
      ]),
      buildRow(LABELS.L, [
        {
          name: isAutoTonometryLegacy ? 'autoTonometryLegacyL' : 'autoTonometryL',
          formatter: !isAutoTonometryLegacy ? formatters.mmHg : null,
        },
        {
          name: isTonometryLegacy ? 'tonometryLegacyL' : 'tonometryL',
          formatter: !isTonometryLegacy ? formatters.mmHg : null,
        },
      ]),
    ];
  }

  return data;
}

export function subjective(inputs) {
  const data = {
    title: 'Refração Subjetiva',
    columns: [
      { label: 'Esférica', highlight: true, align: 'center' },
      { label: 'Cilíndrica', highlight: true, align: 'center' },
      { label: 'Eixo', highlight: false, align: 'center' },
      { label: 'AV CC', highlight: false, align: 'center' },
      { label: 'Adição', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('refSubj')) {
    const buildRow = makeRowBuilder(inputHelper);

    if (inputHelper.hasFilledWith('refSubjLegacy')) {
      data.rows = [
        buildRow(LABELS.R, [{ name: 'refSubjLegacyR' }]),
        buildRow(LABELS.L, [{ name: 'refSubjLegacyL' }]),
      ];
    } else {
      data.rows = [
        buildRow(LABELS.R, [
          { name: 'refSubjSphericalR', formatter: formatters.number },
          { name: 'refSubjCylindricalR', formatter: formatters.number },
          { name: 'refSubjAxisR', formatter: formatters.deg },
          { name: 'refSubjAvccR' },
          { name: 'refSubjAddition', formatter: formatters.number },
        ]),
        buildRow(LABELS.L, [
          { name: 'refSubjSphericalL', formatter: formatters.number },
          { name: 'refSubjCylindricalL', formatter: formatters.number },
          { name: 'refSubjAxisL', formatter: formatters.deg },
          { name: 'refSubjAvccL' },
          { name: 'refSubjQuantity' },
        ]),
      ];
    }
  }

  return data;
}

export function objective(inputs) {
  const data = {
    title: 'Refração Estática',
    columns: [
      { label: 'Esférica', highlight: true, align: 'center' },
      { label: 'Cilíndrica', highlight: true, align: 'center' },
      { label: 'Eixo', highlight: false, align: 'center' },
      { label: 'AV CC', highlight: false, align: 'center' },
      { label: 'Adição', highlight: false, align: 'center' },
    ],
    rows: [],
  };

  const inputHelper = useInputs(inputs);

  if (inputHelper.hasFilledWith('refObj')) {
    const buildRow = makeRowBuilder(inputHelper);

    if (inputHelper.hasFilledWith('refObjLegacy')) {
      data.rows = [
        buildRow(LABELS.R, [{ name: 'refObjLegacyR' }]),
        buildRow(LABELS.L, [{ name: 'refObjLegacyL' }]),
      ];
    } else {
      data.rows = [
        buildRow(LABELS.R, [
          { name: 'refObjSphericalR', formatter: formatters.number },
          { name: 'refObjCylindricalR', formatter: formatters.number },
          { name: 'refObjAxisR', formatter: formatters.deg },
          { name: 'refObjAvccR' },
          { name: 'refObjAddition', formatter: formatters.number },
        ]),
        buildRow(LABELS.L, [
          { name: 'refObjSphericalL', formatter: formatters.number },
          { name: 'refObjCylindricalL', formatter: formatters.number },
          { name: 'refObjAxisL', formatter: formatters.deg },
          { name: 'refObjAvccL' },
          { name: 'refObjQuantity' },
        ]),
      ];
    }
  }

  return data;
}
